/** The campaign a presale place belongs to — one home for the market, its launch day, and its gate. */
export type PresaleCampaign = {
  /** Copy-facing market name ("Loudoun County"). */
  marketName: string;
  /** ISO calendar date (YYYY-MM-DD) service opens in the market. */
  launchDate: string;
  /** Size of the founding group the referral progress bar fills toward. */
  spotsTotal: number;
  /**
   * Homes reserved so far, as ops publishes it.
   *
   * A stand-in, not a measurement: counting the founding group live needs
   * `GET /memberships/founding-count`, which is specified but not built (decision D3,
   * docs/plans/presale-launch). Until it ships this is the number the progress bar
   * shows, and bumping it is a one-line edit rather than a fabricated query.
   */
  spotsReservedFallback: number;
  /** Place zipcodes whose post-checkout confirmation is `/presale` instead of onboarding. */
  zipcodes: readonly string[];
  /**
   * Market token carried by the key of every one of the campaign's `CheckoutLink` rows —
   * the only thing that tells the campaign's offers apart from every other link in the
   * catalog. Matched anywhere in the key, case-insensitively, so the membership type the
   * link sells is not part of the rule: `core-membership-loudon-750` and
   * `premier-membership-loudon-750` both belong to the campaign, and a membership type
   * added later needs no code change. Launching the next market is likewise a data change
   * (new links + a new token).
   */
  checkoutLinkKeyToken: string;
};

/**
 * The Loudoun-area founding-member presale (launch plan, Slack 2026-08-21).
 *
 * The zipcodes are the gate: a place in this list lands on `/presale` after checkout
 * until `launchDate`, when the market opens and members go through `/my/onboarding`.
 */
export const PRESALE_CAMPAIGN: PresaleCampaign = {
  marketName: "Loudoun County",
  launchDate: "2026-09-15",
  spotsTotal: 50,
  spotsReservedFallback: 32,
  checkoutLinkKeyToken: "loudon",
  zipcodes: [
    "20105",
    "20120",
    "20121",
    "20124",
    "20147",
    "20148",
    "20151",
    "20152",
    "20153",
    "20164",
    "20165",
    "20166",
    "20170",
    "20171",
    "20175",
    "20176",
    "20190",
    "20191",
    "20194",
    "20854",
    "20878",
    "22030",
    "22031",
    "22032",
    "22033",
    "22041",
    "22042",
    "22043",
    "22044",
    "22046",
    "22066",
    "22101",
    "22102",
    "22124",
    "22180",
    "22181",
    "22182",
  ],
};

/**
 * The value written to `PurchaseIntent.payload.flow` by the presale funnel, and the only
 * thing that tells a presale intent apart from a join-flow one — both funnels create their
 * intent through the same `POST /places/add-my-place`, which records nothing about which
 * one called it.
 *
 * Campaign-independent on purpose: the NEXT market's presale is new `CheckoutLink` rows and
 * a new zipcode list, still this same funnel, so it keeps this same flow.
 */
export const PRESALE_PURCHASE_INTENT_FLOW = "presale";

/** Five-digit ZIP — ZIP+4 ("20147-1234") and surrounding whitespace collapse to it. */
function normalizeZipcode(zipcode: string): string {
  return zipcode.trim().slice(0, 5);
}

/** Whether a place's zipcode puts it in the presale campaign. */
export function isPresaleZipcode(
  zipcode: string | null | undefined,
  campaign: PresaleCampaign = PRESALE_CAMPAIGN,
): boolean {
  if (!zipcode) return false;
  return campaign.zipcodes.includes(normalizeZipcode(zipcode));
}

/**
 * Whether a `CheckoutLink` belongs to the campaign.
 *
 * The market token appearing anywhere in the key is the only thing telling the campaign's
 * offers apart from every other link in the catalog, so this is what identifies a
 * `PurchaseIntent` as a presale one — the zipcode cannot, because several campaign zips are
 * already-served market (see `shouldGateOnboardingForPresale` in `presale-route.ts`).
 *
 * Deliberately not a prefix match: the key's leading segment is the membership type the link
 * sells, and every membership type sold into the market is the same campaign.
 */
export function isPresaleCheckoutLinkKey(
  key: string | null | undefined,
  campaign: PresaleCampaign = PRESALE_CAMPAIGN,
): boolean {
  if (!key) return false;
  return key
    .toLowerCase()
    .includes(campaign.checkoutLinkKeyToken.toLowerCase());
}

/**
 * Whether the presale window is still open — strictly before launch day, compared as
 * calendar dates in the member's own timezone.
 */
export function isPresaleActive(
  now: Date,
  campaign: PresaleCampaign = PRESALE_CAMPAIGN,
): boolean {
  const today = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return today < campaign.launchDate;
}

/** "September 15" — read as a calendar date so no timezone can shift the day. */
export function formatLaunchDate(launchDate: string): string {
  return formatLaunchDateParts(launchDate, { month: "long", day: "numeric" });
}

/**
 * "September 15, 2026" — the year-bearing form, for the billing schedule.
 *
 * A date the buyer will be charged on has to carry its year; the bare
 * `formatLaunchDate` is for prose where the year is already obvious from context.
 */
export function formatLaunchDateWithYear(launchDate: string): string {
  return formatLaunchDateParts(launchDate, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatLaunchDateParts(
  launchDate: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const [year, month, day] = launchDate.slice(0, 10).split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", options).format(
    new Date(year, month - 1, day),
  );
}
